/**
 * Echo Hunt -- the judge loop (P10, §9).
 *
 * ## The problem this solves
 *
 * A round is only worth playing if a reader with NO knowledge of the user's dreams cannot separate the
 * journal bullets from the generated ones (§2 invariant #1: "stranger-proof, or it isn't scored"). So
 * before a round is shown, a separate judge -- blind to provenance, blind to the corpus -- scores every
 * bullet, and the system (which does know the truth) measures how well it did. If it did too well, the
 * round is repaired and re-judged. Whatever the user scores ABOVE that judge is their own familiarity.
 *
 * ## Metric (§9.2)
 *
 * Two numbers come out of the same scores:
 *  - **AUC** -- `P(a real bullet is scored above a fake one)`. Independent of X, which is why it is the
 *    headline. The loop bands on this.
 *  - **miscategorization %** -- the §9.1 top-X-bucket number. Logged alongside, because it is what §9.1
 *    literally specified, but it is NOT the band: it has a ceiling of `2X(1-X)` (50% at X = .5, 42% at
 *    X = .3), so a fixed target on it means different things at different X (§9.2, `Scoring.ts`).
 *
 * The band is TWO-SIDED. A judge scoring BELOW it is confidently inverted, which is a tell in the other
 * direction -- "consistently wrong about which of these is mine" tells a reader as much as being right.
 *
 * ## Repair menu (§9.3)
 *
 * The repair is chosen by WHICH CLASS is giving the judge most of its accuracy: if it keeps confidently
 * spotting fakes, resample a fake; if it keeps confidently spotting reals, repair a real (in-place
 * rewrite toward the world's lore, or resample it). The point is that no class owns the repair -- if only
 * real bullets got a "fit the lore better" pass, "suspiciously lore-fitting" would become a cross-round
 * tell pointing at the truth (§17).
 *
 * The weaver is NOT re-called (§9.3): the repair rewrite is in place, so the expensive call happens once
 * per round.
 *
 * ## Hard stop (§9.4)
 *
 * After `maxAttempts` rounds the loop gives up and the round becomes `easy` or `discarded` (the caller
 * decides which). The bullets that resisted hiding are returned, because they are the most
 * stranger-detectable kinds of the user's dream content -- a §13.3 by-product in its own right.
 *
 * ## Held-out judge (§9.5)
 *
 * One call on the FINAL story, from a model that was never used in the loop, logged only. Without it the
 * loop's own band is a Goodhart target: a judge that has been repaired against is no longer an honest
 * estimate of leakage.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The LLM is passed in as an
 * `LLMBackend`, so tests use `MockLLMBackend`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §9, §2 (#1, #2), §13.1, §16.2 P10.
 */
import {CallLLM, LLMBackend, LLMCallCounter, PreviewText, ValidateJSON} from "./LLM.js";
import {CheckBullet, CheckBulletOptions} from "./Normalize.js";
import {ComputeAUC, RandomJudgeMiscategorizationPct} from "./Scoring.js";
import {Judge_JSONSchema, Judge_SystemPrompt, Judge_UserPrompt} from "./Prompts/Judge.js";
import {
	RepairRewrite_JSONSchema,
	RepairRewrite_SystemPrompt,
	RepairRewrite_UserPrompt,
} from "./Prompts/RepairRewrite.js";
import {
	EchoCharacter,
	EchoJudgeSettings,
	EchoWorld,
	JudgeAttempt,
	JudgeLoopSummary,
	JudgeRepair,
	RoundSlot,
	SlotRepairAttempt,
} from "./Types.js";

// settings (§9.1, §9.2, §9.3)
// ==========

/** Default two-sided AUC band (§16.1 "AUC band 0.4-0.6"). */
export const DEFAULT_JUDGE_BAND_MIN = .4;
export const DEFAULT_JUDGE_BAND_MAX = .6;

/** Default repair-loop rounds before the hard stop (§9.4). Three is enough for two repairs while keeping
 * the paid calls per round bounded -- the loop re-judges twice per attempt when two judge models are set. */
export const DEFAULT_JUDGE_MAX_ATTEMPTS = 3;

/** Default in-place repair rewrites allowed per slot (§9.3 "budget of about 2 rewrites per bullet before
 * falling back to resampling"). */
export const DEFAULT_REWRITE_BUDGET_PER_SLOT = 2;

/** Default number of judge calls per round in the loop (§9.2 "2 judge calls or 2 model families, each
 * required to fall inside the band"). Two independent readings is the cheapest way to stop one noisy
 * judge from flipping a round's difficulty; more would multiply the cost of every repair attempt. */
export const DEFAULT_LOOP_JUDGE_CALLS = 2;

export type JudgeLoopSettings = {
	bandMin: number;
	bandMax: number;
	/** models for the LOOP judges; when shorter than the number of calls, the list cycles */
	judgeModels: string[];
	/** total judge calls per attempt (§9.2). Each must land in the band. */
	loopJudgeCalls: number;
	maxAttempts: number;
	rewriteBudgetPerSlot: number;
	/** model for the HELD-OUT judge; "" lets the backend/settings decide (§9.5) */
	heldOutJudgeModel?: string|n;
};

export function DefaultJudgeLoopSettings(): JudgeLoopSettings {
	return {
		bandMin: DEFAULT_JUDGE_BAND_MIN,
		bandMax: DEFAULT_JUDGE_BAND_MAX,
		judgeModels: [],
		loopJudgeCalls: DEFAULT_LOOP_JUDGE_CALLS,
		maxAttempts: DEFAULT_JUDGE_MAX_ATTEMPTS,
		rewriteBudgetPerSlot: DEFAULT_REWRITE_BUDGET_PER_SLOT,
		heldOutJudgeModel: null,
	};
}

/** The PROFILE-side default for `echoProfiles.judge` (§9.1-§9.3, §5.5): the loop defaults plus the two
 * knobs that only exist on the profile (rejection sampling). Lives in this PURE module rather than in a
 * `*_Live.ts` wrapper so the profile-creation path and the loop wrapper can both read it without a
 * circular import.
 *
 * Rejection sampling is OFF by default on purpose (§5.5 "optional rejection sampling ... behind a setting
 * that defaults to off"): it multiplies the paid generator calls per fake for a benefit nobody has
 * measured yet. */
export function DefaultEchoJudgeSettings(): EchoJudgeSettings {
	const base = DefaultJudgeLoopSettings();
	return {
		bandMin: base.bandMin,
		bandMax: base.bandMax,
		judgeModels: [],
		maxAttempts: base.maxAttempts,
		rewriteBudgetPerSlot: base.rewriteBudgetPerSlot,
		rejectionSampling: false,
		rejectionSamplingCandidates: 3,
	};
}

/** Normalises a band so `min <= max` and both sit in `[0, 1]`. A swapped or absurd pair from a settings
 * field must not turn into a band nothing can satisfy (which would silently `discard` every round). */
export function NormalizeBand(bandMin: number, bandMax: number): {min: number, max: number} {
	const clamp = (value: number)=>Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : .5;
	const low = clamp(bandMin);
	const high = clamp(bandMax);
	return low <= high ? {min: low, max: high} : {min: high, max: low};
}

// the metric (§9.1, §9.2)
// ==========

export type JudgeMetric = {
	/** AUC over the scores against the true labels (§9.2) */
	auc: number;
	/** top-X bucket miscategorization, in PERCENT (§9.1, §9.2) */
	miscategorizationPct: number;
	/** the ceiling `2X(1-X)*100` -- what an information-free judge would reach. Logged and shown beside the
	 * measured number so a reader can tell "72% is good" from "72% is worse than chance at this X". */
	chanceMiscategorizationPct: number;
	/** the X that was used (real share), recorded so the % stays interpretable */
	xShare: number;
	/** did the AUC land inside the band? */
	inBand: boolean;
	/** the per-class accuracy split, which is what §9.3's repair choice reads */
	byClass: JudgeClassSplit;
};

/** How the judge's discrimination splits by class, which is what picks the repair (§9.3): a judge reading
 * the FAKES needs a better fake, one reading the REALS needs a real bullet that stops standing out.
 *
 * Measured as a SIGNED contribution rather than as a per-class accuracy, because per-class accuracy on the
 * top-X bucket is degenerate: with balanced classes at X = .5 the two accuracies are algebraically the same
 * number, so they can never say which side is the tell. Each class instead contributes how far its mean
 * confidence sits in the CORRECT direction (`real - .5` and `.5 - fake`). A coin-flip judge gives ~0 for
 * both, which is right: there is no signal to attribute, so the repair choice falls back to alternating. */
export type JudgeClassSplit = {
	meanConfidenceReal: number;
	meanConfidenceFake: number;
	/** > 0 means the judge is reading reals as dream-sourced (the real-sided tell) */
	signalFromReal: number;
	/** > 0 means the judge is reading fakes as generated (the fake-sided tell) */
	signalFromFake: number;
};

/** Computes both numbers (§9.1, §9.2) from a judge's scores and the round's true labels.
 *
 * The "top-X bucket" is computed the way §9.1 describes it for a JUDGE (not for the player): the judge's
 * X highest-scored slots are its "dream" bucket, and the system knows which of those were wrong. Unlike
 * the player's marking this is only ever a diagnostic, because AUC is the band.
 */
export function ComputeJudgeMetric(scores: Record<string, number>|n, slots: {slotId: string, sourceKind: "real"|"fake"}[]|n, band: {min: number, max: number}, options?: {xShare?: number|n}): JudgeMetric {
	const slotList = slots ?? [];
	const scoreMap = scores ?? {};

	const labelled = slotList.map(slot=>({
		slotId: slot.slotId,
		isReal: slot.sourceKind == "real",
		// a missing score means the judge said nothing about that bullet; .5 is the honest reading
		score: ClampScore(scoreMap[slot.slotId]),
	}));

	const realCount = labelled.filter(a=>a.isReal).length;
	const xShare = options?.xShare != null && Number.isFinite(options.xShare)
		? Math.max(0, Math.min(1, options.xShare))
		: (labelled.length == 0 ? 0 : realCount / labelled.length);

	const auc = ComputeAUC(labelled.map(a=>({confidence: a.score * 100, isReal: a.isReal})));

	// the top-X bucket: the judge's X highest-scored slots are what it claims are dreams
	const bucketSize = Math.max(0, Math.min(labelled.length, Math.round(labelled.length * xShare)));
	const ranked = [...labelled].sort((a, b)=>b.score - a.score || a.slotId.localeCompare(b.slotId));
	const bucket = new Set(ranked.slice(0, bucketSize).map(a=>a.slotId));

	const wrong = labelled.filter(a=>bucket.has(a.slotId) != a.isReal).length;
	const miscategorizationPct = labelled.length == 0 ? 0 : (wrong / labelled.length) * 100;

	const realSlots = labelled.filter(a=>a.isReal);
	const fakeSlots = labelled.filter(a=>!a.isReal);
	const meanConfidenceReal = Mean(realSlots.map(a=>a.score));
	const meanConfidenceFake = Mean(fakeSlots.map(a=>a.score));

	return {
		auc,
		miscategorizationPct,
		chanceMiscategorizationPct: RandomJudgeMiscategorizationPct(xShare),
		xShare,
		inBand: auc >= band.min && auc <= band.max,
		byClass: {
			meanConfidenceReal,
			meanConfidenceFake,
			signalFromReal: meanConfidenceReal - .5,
			signalFromFake: .5 - meanConfidenceFake,
		},
	};
}

function ClampScore(value: number|n): number {
	if (value == null || !Number.isFinite(value)) return .5;
	return Math.max(0, Math.min(1, value));
}

function Mean(values: number[]): number {
	if (values.length == 0) return .5;
	return values.reduce((sum, value)=>sum + value, 0) / values.length;
}

// repair choice (§9.3)
// ==========

/** Which slot a repair should target, and with which repair. `null` when there is nothing sensible to do
 * (e.g. no fakes at all, or every repair budget is spent) -- the caller then treats the attempt as the
 * last one rather than looping forever. */
export type RepairPlan = {
	repair: JudgeRepair;
	/** the slot to repair; null for `none` */
	slotId: string|null;
	/** why this repair, in plain language, for the log and the dev tool */
	reason: string;
};

/** The minimal slot shape `ChooseRepair` reads: its label, and how many rewrites it has already had. */
export type JudgeLoopSlotChoice = {
	slotId: string;
	sourceKind: "real"|"fake";
	rewriteCount?: number|n;
};

/** Chooses the next repair (§9.3). The rule is stated in terms of the judge's per-class behaviour rather
 * than in terms of the labels alone, because the goal is to remove the SIGNAL the judge is using:
 *
 *  1. If the judge is more accurate on FAKES, fakes are the tell -- resample the fake it was most sure
 *     about. (A confident fake is the one whose fingerprint is easiest to read.)
 *  2. If it is more accurate on REALS, the real bullets are the tell -- repair the real it was most sure
 *     about. Prefer an in-place rewrite while that slot still has budget (cheap, and no re-weave), then
 *     resample.
 *  3. If both sides are equally good, alternate so neither class accumulates all the repairs across a
 *     session (§17 one-sided-repair tell). The choice is by attempt parity, seeded by `attemptIndex`, so it
 *     is deterministic and testable.
 *
 * `resistingSlotIds` (the confidently-classified slots) are passed in already computed, so this function
 * stays a pure decision over a small summary rather than re-deriving anything.
 */
export function ChooseRepair(options: {
	metric: JudgeMetric;
	/** slots, with their label and repair bookkeeping */
	slots: JudgeLoopSlotChoice[];
	/** the judge's scores, so "most confident" can be read off */
	scores: Record<string, number>|n;
	/** 0-based repair-loop round; used for the parity tiebreak */
	attemptIndex: number;
	/** max in-place rewrites per slot (§9.3) */
	rewriteBudgetPerSlot: number;
}): RepairPlan {
	const {metric, slots, scores, attemptIndex, rewriteBudgetPerSlot} = options;
	const scoreMap = scores ?? {};

	const fakes = slots.filter(a=>a.sourceKind == "fake");
	const reals = slots.filter(a=>a.sourceKind == "real");
	const confWithin = (slot: JudgeLoopSlotChoice)=>{
		const score = ClampScore(scoreMap[slot.slotId]);
		// confidence in the judge's own verdict, so "most spotted" is comparable across classes
		return slot.sourceKind == "fake" ? 1 - score : score;
	};
	const mostConfident = (list: JudgeLoopSlotChoice[])=>
		[...list].sort((a, b)=>confWithin(b) - confWithin(a) || a.slotId.localeCompare(b.slotId))[0] ?? null;

	const realAdvantage = metric.byClass.signalFromReal - metric.byClass.signalFromFake;
	// a difference inside this tolerance is noise, not a signal -- two classes of 3-5 bullets each produce
	// coarse means, so a small difference is not evidence of which class is the tell
	const CLASS_ADVANTAGE_TOLERANCE = .05;

	const preferFake = fakes.length > 0 && (realAdvantage < -CLASS_ADVANTAGE_TOLERANCE || (Math.abs(realAdvantage) <= CLASS_ADVANTAGE_TOLERANCE && attemptIndex % 2 == 1));
	const preferReal = reals.length > 0 && !preferFake;

	if (preferFake) {
		const slot = mostConfident(fakes);
		if (slot != null) {
			return {repair: "resampleFake", slotId: slot.slotId, reason: `the judge was most confident about this fake (p=${ClampScore(scoreMap[slot.slotId]).toFixed(2)})`};
		}
	}
	if (preferReal) {
		const slot = mostConfident(reals);
		if (slot != null) {
			const used = Math.max(0, Math.round(slot.rewriteCount ?? 0));
			const canRewrite = used < rewriteBudgetPerSlot;
			return canRewrite
				? {repair: "rewriteReal", slotId: slot.slotId, reason: `the judge was most confident about this real bullet (p=${ClampScore(scoreMap[slot.slotId]).toFixed(2)}); rewrite ${used + 1}/${rewriteBudgetPerSlot}`}
				: {repair: "resampleReal", slotId: slot.slotId, reason: `the judge was most confident about this real bullet (p=${ClampScore(scoreMap[slot.slotId]).toFixed(2)}), and its rewrite budget is spent`};
		}
	}

	return {repair: "none", slotId: null, reason: "there was no slot of the relevant class left to repair"};
}

/** The slots the judge classified most confidently AND correctly (§9.4 "log the bullets that resisted
 * hiding: these are the most stranger-detectable kinds of your dream content").
 *
 * Only REALS are returned: a fake the judge spotted says something about the fake, not about the user's
 * dreams, and §9.4's by-product is about the user's content. */
export function SelectResistingSlotIds(scores: Record<string, number>|n, slots: {slotId: string, sourceKind: "real"|"fake"}[]|n, threshold = .5): string[] {
	const scoreMap = scores ?? {};
	return (slots ?? [])
		.filter(slot=>slot.sourceKind == "real" && ClampScore(scoreMap[slot.slotId]) >= threshold)
		.sort((a, b)=>ClampScore(scoreMap[b.slotId]) - ClampScore(scoreMap[a.slotId]))
		.map(slot=>slot.slotId);
}

// judging one story (§9.1)
// ==========

/** One bullet as the judge may see it: an id and its text, and nothing else (§16.0). */
export type JudgeBullet = {
	slotId: string;
	text: string;
};

export type JudgeResult = {
	/** P(dream-sourced) per slot id; empty when the call produced nothing usable */
	scores: Record<string, number>;
	/** the problems found (id mismatch, out-of-range values), for the retry and the log */
	problems: string[];
	outputTokens: number;
	model: string;
};

/** Parses a judge reply. Salvages per-entry problems and reports them, so the model gets one coherent
 * list to fix (§9.1) instead of a schema error it cannot act on. */
export function ParseJudgeReply(parsed: unknown): {scores: Record<string, number>, problems: string[]} {
	const problems: string[] = [];
	const scores: Record<string, number> = {};

	if (parsed == null || typeof parsed != "object" || Array.isArray(parsed)) {
		return {scores, problems: ["The reply was not a JSON object."]};
	}
	const obj = parsed as Record<string, unknown>;
	if (!Array.isArray(obj.scores)) {
		return {scores, problems: ["The reply has no `scores` array."]};
	}

	for (const [index, entry] of obj.scores.entries()) {
		if (entry == null || typeof entry != "object") {
			problems.push(`scores[${index}] is not an object.`);
			continue;
		}
		const item = entry as Record<string, unknown>;
		const id = typeof item.id == "string" ? item.id.trim() : "";
		if (id.length == 0) {
			problems.push(`scores[${index}] has no \`id\`.`);
			continue;
		}
		const raw = typeof item.p == "number" ? item.p : (typeof item.p == "string" ? Number(item.p) : NaN);
		if (!Number.isFinite(raw)) {
			problems.push(`scores[${index}] (id ${id}) has no numeric \`p\`.`);
			continue;
		}
		// a percentage is a common near-miss and is unambiguous, so it is salvaged rather than retried
		const normalized = raw > 1 && raw <= 100 ? raw / 100 : raw;
		if (normalized < 0 || normalized > 1) {
			problems.push(`scores[${index}] (id ${id}) has \`p\` = ${raw}, which is not between 0 and 1.`);
			continue;
		}
		if (id in scores) {
			problems.push(`scores[${index}] repeats the id "${id}".`);
			continue;
		}
		scores[id] = normalized;
	}

	return {scores, problems};
}

/** Checks a parsed reply against the bullets it was asked about (§9.1: one entry per id, exactly once).
 * A missing id is a real problem -- the judge's score for it would otherwise default to .5 and quietly
 * pull AUC toward chance, making a too-easy round look acceptable. */
export function ValidateJudgeScores(bullets: JudgeBullet[]|n, scores: Record<string, number>|n): string[] {
	const problems: string[] = [];
	const expected = (bullets ?? []).map(a=>a.slotId);
	const got = Object.keys(scores ?? {});

	const missing = expected.filter(id=>!(id in (scores ?? {})));
	const unexpected = got.filter(id=>!expected.includes(id));
	if (missing.length > 0) problems.push(`Missing a score for ${missing.length} bullet(s): ${missing.join(", ")}.`);
	if (unexpected.length > 0) problems.push(`Scored ${unexpected.length} id(s) that were not in the installment: ${unexpected.join(", ")}.`);
	if (expected.length > 0 && got.length != expected.length) {
		problems.push(`Returned ${got.length} score(s) for ${expected.length} bullet(s). Never merge, drop or invent entries.`);
	}

	return problems;
}

/** One judge call (§9.1). Blind to provenance and to the corpus -- see `Prompts/Judge.ts` for how that is
 * structural rather than a matter of discipline. */
export async function JudgeStory(backend: LLMBackend, options: {
	bullets: JudgeBullet[];
	model?: string|n;
	counter?: LLMCallCounter|n;
	maxAttempts?: number;
}): Promise<JudgeResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const bullets = options.bullets ?? [];

	let outputTokens = 0;
	let lastProblems: string[] = [];
	let fixNotes: string|n = null;
	let lastModel = options.model ?? "";

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const result = await CallLLM(backend, {
			role: "judge",
			model: options.model ?? null,
			system: Judge_SystemPrompt(),
			user: Judge_UserPrompt({
				// `{id, text}` only: there is no field through which a label could be passed (§16.0)
				bullets: bullets.map(bullet=>({id: bullet.slotId, text: bullet.text})),
				fixNotes,
			}),
			json: Judge_JSONSchema(),
			counter: options.counter ?? null,
		});
		outputTokens += result.outputTokens;
		lastModel = result.model || lastModel;

		if (result.error != null) {
			lastProblems = [result.error];
			fixNotes = result.error;
			continue;
		}

		const parsed = ParseJudgeReply(result.parsed);
		const problems = [...parsed.problems, ...ValidateJudgeScores(bullets, parsed.scores)];
		if (problems.length > 0) {
			lastProblems = problems;
			fixNotes = problems.join("\n");
			continue;
		}

		return {scores: parsed.scores, problems: [], outputTokens, model: lastModel};
	}

	return {scores: {}, problems: lastProblems.length > 0 ? lastProblems : ["the judge produced no usable scores"], outputTokens, model: lastModel};
}

// the in-place repair (§9.3)
// ==========

export type RepairRewriteResult = {
	/** the rewritten target text, or null when every attempt failed */
	text: string|null;
	problems: string[];
	outputTokens: number;
};

/** Rewrites ONE real bullet in place, so it fits the story's world better without changing its event
 * (§9.3). The retention check is the CALLER's job (`judgeLoop` below runs it), because it needs the
 * source event text, which is not something this module's inputs have.
 *
 * The woven text of the other bullets is passed in unchanged so the model can see what the target has to
 * join. They are NOT returned by the model (the schema asks only for `text`), which is why there is no
 * "did it preserve the others" check: the model never got the chance to change them.
 *
 * A NO-OP reply (the model hands the sentence back unchanged) is treated as a failed attempt, not a
 * success. It passes every other check trivially -- it is valid JSON, non-empty, and it cannot reintroduce
 * a dash that was not already there -- so without this the loop records `applied: true, "rewritten in
 * place"` for a rewrite that rewrote nothing. That is the exact shape of "my retries show no new text":
 * the attempt really did run and really was stored, but `textAfter == textBefore`, so there is nothing to
 * show and the sandbox text never moves. Retried with an explicit fix-note, because "leave it as it is" is
 * usually the model misreading the task rather than a considered answer; if every attempt still echoes,
 * the caller gets `text: null` with the reason below and the history says so.
 */
export async function RepairRewrite(backend: LLMBackend, options: {
	world: EchoWorld;
	character: EchoCharacter;
	summary?: string|n;
	bullets: {slotId: string, text: string}[];
	targetSlotId: string;
	model?: string|n;
	counter?: LLMCallCounter|n;
	checkOptions?: CheckBulletOptions|n;
	maxAttempts?: number;
}): Promise<RepairRewriteResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const {checkOptions} = options;
	/** the sentence being replaced; a reply identical to this changed nothing */
	const currentText = (options.bullets.find(bullet=>bullet.slotId == options.targetSlotId)?.text ?? "").trim();

	let outputTokens = 0;
	let lastProblems: string[] = [];
	let fixNotes: string|n = null;
	let echoedUnchanged = false;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const result = await CallLLM(backend, {
			role: "rewriter",
			model: options.model ?? null,
			system: RepairRewrite_SystemPrompt(),
			user: RepairRewrite_UserPrompt({
				worldName: options.world.name,
				worldBible: options.world.bible,
				characterName: options.character.name,
				summary: options.summary,
				bullets: options.bullets.map(bullet=>({id: bullet.slotId, text: bullet.text})),
				targetId: options.targetSlotId,
				fixNotes,
			}),
			json: RepairRewrite_JSONSchema(),
			counter: options.counter ?? null,
		});
		outputTokens += result.outputTokens;

		const text = typeof (result.parsed as {text?: unknown}|n)?.text == "string"
			? ((result.parsed as {text: string}).text).trim()
			: "";
		if (text.length == 0) {
			lastProblems = [result.error ?? "the reply had no non-empty `text`"];
			fixNotes = lastProblems.join("\n");
			continue;
		}

		// the §7 gate: a rewrite is new prose and can reintroduce the user's dash habit or a banned term
		const violations = CheckBullet(text, checkOptions ?? {}).map(a=>`${a.kind}: ${a.message}`);
		if (violations.length > 0) {
			lastProblems = violations;
			fixNotes = violations.join("\n");
			continue;
		}

		// a rewrite that returns the sentence unchanged did nothing. Reported (and retried) rather than
		// accepted: accepting it records an `applied` attempt whose `textAfter` equals its `textBefore`, which
		// reads in the inspector as "the retry produced no text" and leaves the sandbox text stuck.
		if (currentText.length > 0 && text == currentText) {
			echoedUnchanged = true;
			lastProblems = ["the reply was the sentence unchanged -- it must be reworded, not repeated"];
			fixNotes = "You returned the target sentence exactly as it was given to you. Reword it. Keep the same event, but change the wording so it sits more naturally in this world.";
			continue;
		}

		return {text, problems: [], outputTokens};
	}

	return {text: null, problems: echoedUnchanged ? ["the model returned the sentence unchanged on every attempt, so no rewrite was applied"] : lastProblems, outputTokens};
}

// the loop (§9.1, §9.3, §9.4, §9.5)
// ==========

/** What the loop needs to know about a slot that the pure types do not carry: its label, its repair
 * bookkeeping, and the source event text the retention check compares against. */
export type JudgeLoopSlot = RoundSlot & {
	/** the indexed source event for a real bullet (for the retention gate on a repair rewrite). Absent for
	 * fakes, which have no source by construction. */
	sourceEventText?: string|n;
};

export type JudgeLoopOptions = {
	slots: JudgeLoopSlot[];
	settings: JudgeLoopSettings;
	world: EchoWorld;
	character: EchoCharacter;
	summary?: string|n;
	xShare: number;
	counter?: LLMCallCounter|n;
	checkOptions?: CheckBulletOptions|n;
	/** Called when a slot's bullet must be RESAMPLED (§9.3). Returns the new text (plus the new corpus row, for
	 * a real), or null when no replacement is available. Injected because resampling needs the
	 * corpus/generator, which this pure module deliberately does not know about. */
	resampleSlot?: ((slot: JudgeLoopSlot, repair: JudgeRepair)=>Promise<ResampleOutcome|null>)|n;
	/** The retention check for a repair rewrite. Injected for the same reason: it needs the source event,
	 * and re-implementing it here would duplicate `Reshape.ts`'s (deliberately asymmetric) failure rule. */
	checkRetention?: ((sourceText: string, rewrittenText: string)=>Promise<boolean>)|n;
	/** model for the held-out judge (§9.5); falls back to `settings.heldOutJudgeModel` */
	heldOutModel?: string|n;
	/** skip the held-out judge call (dev tool / tests that only care about the loop) */
	skipHeldOut?: boolean;
	/** Where new `repairHistory` entries get their `attemptIndex` from. The automatic loop starts at 0; a
	 * MANUAL retry from the debug view passes `existingSummary.attempts` so its entry continues the numbering
	 * instead of overwriting the history the user is looking at. */
	attemptIndexOffset?: number|n;
	/** Set FALSE to judge only, never repair. A manual retry needs this: it has already applied the repair the
	 * user chose, and the question being asked is "what does the judge think NOW" (§9.3). Without it, a
	 * single-attempt re-judge that lands out of band applies a SECOND, loop-chosen repair -- which is how a
	 * `loop` attempt appeared in the history for a button the user never pressed. */
	allowRepairs?: boolean|n;
};

/** What a resample callback produced (§9.3). Returning an object rather than a bare string lets the loop
 * record WHY an attempt failed, which is the difference between "the loop tried and it worked" and "the
 * loop tried and nothing came back" -- invisible in the round-level counts, obvious in the debug view. */
export type ResampleOutcome = {
	text: string;
	/** the corpus row the replacement came from (real bullets only) */
	newSourceId?: string|n;
	outputTokens?: number|n;
};

export type JudgeLoopResult = {
	/** the slots after any repairs, in story order. `finalText` carries the repaired text where a rewrite
	 * landed, so the caller can persist these straight back onto the round. */
	slots: JudgeLoopSlot[];
	/** every judge call, in order, ready to be appended to the round's `judgeLog` */
	judgeLog: JudgeAttempt[];
	/** the loop's summary, for the round and the reveal */
	summary: JudgeLoopSummary;
	/** the round status the loop implies (§9.4): `ok` when it landed in the band, otherwise the caller's
	 * choice of `easy`/`discarded` */
	status: "ok"|"easy";
	outputTokens: number;
};

/** Runs the judge loop over one woven story (§9.1-§9.5).
 *
 * Per attempt:
 *  1. every loop judge scores the story (2 calls by default, each required to be in the band);
 *  2. the metrics are computed and logged;
 *  3. if EVERY call is in the band, the loop stops successfully;
 *  4. otherwise a repair is chosen (§9.3) and applied -- a rewrite goes through the retention gate, a
 *     resample asks the injected callback -- and the next attempt judges the repaired story.
 *
 * The weaver is never re-called (§9.3): repairs are in place, so the story's ORDER never changes and the
 * expensive call happens once per round.
 *
 * A failed repair does NOT abort the loop: if the injected callbacks are absent or produce nothing, the
 * attempt simply consumes a round and the loop ends at the hard stop, which marks the round `easy`. That
 * keeps "the round could not be made hard enough" and "the repair machinery is unavailable" from being
 * the same failure.
 */
export async function RunJudgeLoop(backend: LLMBackend, options: JudgeLoopOptions): Promise<JudgeLoopResult> {
	const {settings} = options;
	const band = NormalizeBand(settings.bandMin, settings.bandMax);
	const maxAttempts = Math.max(1, Math.round(settings.maxAttempts));
	const loopJudgeCalls = Math.max(1, Math.round(settings.loopJudgeCalls));

	const judgeModels = settings.judgeModels.map(a=>a.trim()).filter(a=>a.length > 0);
	const judgeLog: JudgeAttempt[] = [];
	const repairs: Partial<Record<JudgeRepair, number>> = {};
	let outputTokens = 0;

	// the slots are mutated in place across attempts: a repair changes OTHER attempts' context too (the
	// judge re-reads the whole story), so each attempt must build its own `JudgeBullet` list from the
	// current texts rather than from a snapshot taken once
	let slots = options.slots.map(slot=>({...slot}));

	let lastMetric: JudgeMetric|null = null;
	let lastScores: Record<string, number> = {};
	let landedInBand = false;
	let attemptsRun = 0;
	let judgeCalls = 0;
	/** where this run's repair-history entries start, so a MANUAL retry continues the numbering rather than
	 * colliding with the history the user is looking at */
	const historyIndexBase = Math.max(0, Math.round(options.attemptIndexOffset ?? 0));
	/** the last metric computed from a judge that ACTUALLY produced scores. Kept apart from `lastMetric`
	 * because a judge that replied with nothing yields a degenerate .5 AUC, which must not be reported as
	 * the round's metric -- it would read as "a coin flip" when the truth is "that judge said nothing". */
	let lastUsableMetric: JudgeMetric|null = null;

	for (let attemptIndex = 0; attemptIndex < maxAttempts; attemptIndex++) {
		attemptsRun = attemptIndex + 1;
		const bullets: JudgeBullet[] = slots.map(slot=>({slotId: slot.slotId, text: SlotDisplayText(slot)}));

		let allInBand = true;
		lastMetric = null;
		lastScores = {};

		for (let callIndex = 0; callIndex < loopJudgeCalls; callIndex++) {
			const model = judgeModels.length == 0 ? null : judgeModels[callIndex % judgeModels.length];
			const judged = await JudgeStory(backend, {
				bullets,
				model,
				counter: options.counter ?? null,
			});
			judgeCalls++;
			outputTokens += judged.outputTokens;

			const metric = ComputeJudgeMetric(judged.scores, slots, band, {xShare: options.xShare});
			const entry: JudgeAttempt = {
				judge: judged.model || (model ?? "judge"),
				scores: judged.scores,
				auc: metric.auc,
				miscategorizationPct: metric.miscategorizationPct,
				repair: null,
				outputTokens: judged.outputTokens,
				attemptIndex,
				inBand: metric.inBand,
				bandMin: band.min,
				bandMax: band.max,
			};
			// a judge that produced no scores at all must not count as "in band": its empty score map
			// would make every bullet .5 and compute an AUC of exactly .5, which is INSIDE any sensible
			// band -- so a total failure would silently pass the loop
			if (judged.problems.length > 0 || Object.keys(judged.scores).length == 0) {
				entry.inBand = false;
				entry.auc = null;
				entry.miscategorizationPct = null;
			}
			judgeLog.push(entry);

			// the last judge's verdict is what the log's summary reports; with two in band they agree
			lastMetric = metric;
			lastScores = judged.scores;
			if (entry.inBand != true) allInBand = false;
			else lastUsableMetric = metric;
		}

		if (allInBand && lastMetric != null) {
			landedInBand = true;
			break;
		}

		// ---- repair (§9.3) ----
		// `allowRepairs: false` means JUDGE ONLY. That is what a manual retry needs: the user already chose and
		// applied the repair, so the loop's job here is to report the result, not to start repairing again.
		if (options.allowRepairs == false) break;
		if (lastMetric == null) break;
		const plan = ChooseRepair({
			metric: lastMetric,
			slots: slots.map(slot=>({slotId: slot.slotId, sourceKind: slot.sourceKind, rewriteCount: slot.rewriteCount})),
			scores: lastScores,
			attemptIndex,
			rewriteBudgetPerSlot: settings.rewriteBudgetPerSlot,
		});
		if (plan.repair == "none" || plan.slotId == null) break;

		// the plan is recorded on the LAST judge entry of the attempt, since it applies to the attempt as a
		// whole rather than to one judge call
		const targetEntry = [...judgeLog].reverse().find(a=>a.attemptIndex == attemptIndex);
		if (targetEntry != null) targetEntry.repair = plan.repair;
		repairs[plan.repair] = (repairs[plan.repair] ?? 0) + 1;

		const slotIndexById = slots.findIndex(slot=>slot.slotId == plan.slotId);
		if (slotIndexById < 0) break;

		// one repair attempt, applied and recorded. Extracted so the automatic loop and the MANUAL retry path
		// (`RepairSlotOnce`) run the exact same code -- a manual retry that behaved differently from the loop
		// would be testing a different function than the one under investigation.
		const outcome = await ApplyRepair(backend, plan.repair, slots, slotIndexById, {
			world: options.world,
			character: options.character,
			summary: options.summary,
			counter: options.counter ?? null,
			checkOptions: options.checkOptions,
			resampleSlot: options.resampleSlot ?? null,
			checkRetention: options.checkRetention ?? null,
			attemptIndex: historyIndexBase + attemptsRun - 1,
			source: "loop",
		});
		outputTokens += outcome.outputTokens;
		slots = outcome.slots;
	}

	// ---- held-out judge (§9.5) ----
	let heldOutAuc: number|null = null;
	let heldOutScores: Record<string, number> = {};
	if (options.skipHeldOut != true && slots.length > 0) {
		const heldOutModel = options.heldOutModel ?? settings.heldOutJudgeModel ?? null;
		const heldOut = await JudgeStory(backend, {
			bullets: slots.map(slot=>({slotId: slot.slotId, text: SlotDisplayText(slot)})),
			model: heldOutModel,
			counter: options.counter ?? null,
		});
		outputTokens += heldOut.outputTokens;
		if (Object.keys(heldOut.scores).length > 0) {
			heldOutScores = heldOut.scores;
			const metric = ComputeJudgeMetric(heldOut.scores, slots, band, {xShare: options.xShare});
			heldOutAuc = metric.auc;
			judgeLog.push({
				judge: "heldOut",
				scores: heldOut.scores,
				auc: metric.auc,
				miscategorizationPct: metric.miscategorizationPct,
				repair: null,
				outputTokens: heldOut.outputTokens,
				attemptIndex: attemptsRun - 1,
				inBand: metric.inBand,
				bandMin: band.min,
				bandMax: band.max,
			});
		}
	}

	// per-slot held-out score, for the round log only (§13.1). Never fed back into the loop.
	if (Object.keys(heldOutScores).length > 0) {
		slots = slots.map(slot=>({...slot, heldoutScore: heldOutScores[slot.slotId] ?? null}));
	}

	const summary: JudgeLoopSummary = {
		landedInBand,
		attempts: attemptsRun,
		judgeCalls,
		auc: lastUsableMetric?.auc ?? null,
		miscategorizationPct: lastUsableMetric?.miscategorizationPct ?? null,
		bandMin: band.min,
		bandMax: band.max,
		repairs,
		resistingSlotIds: SelectResistingSlotIds(lastScores, slots),
		heldOutAuc,
		outputTokens,
	};

	return {
		slots,
		judgeLog,
		summary,
		status: landedInBand ? "ok" : "easy",
		outputTokens,
	};
}

// helpers
// ==========

export type ApplyRepairOptions = {
	world: EchoWorld;
	character: EchoCharacter;
	summary?: string|n;
	counter?: LLMCallCounter|n;
	checkOptions?: CheckBulletOptions|n;
	resampleSlot?: JudgeLoopOptions["resampleSlot"];
	checkRetention?: JudgeLoopOptions["checkRetention"];
	/** the `attemptIndex` the history entry gets (§9.3, `SlotRepairAttempt`) */
	attemptIndex: number;
	/** `loop` when the automatic loop chose it, `manual` when the user pressed a retry button */
	source: "loop"|"manual";
};

export type ApplyRepairResult = {
	/** the slots after the attempt (a new array; the input is never mutated) */
	slots: JudgeLoopSlot[];
	/** true when the repair actually changed the slot's text */
	applied: boolean;
	/** the history entry that was appended to the target slot */
	history: SlotRepairAttempt;
	outputTokens: number;
};

/** Applies ONE repair to ONE slot, records it on the slot's `repairHistory`, and reports whether it landed
 * (§9.3).
 *
 * Extracted from `RunJudgeLoop` so a MANUAL retry from the debug view runs the identical code path: a manual
 * retry that behaved differently from the loop would be investigating a different function than the one
 * that produced the stubborn bullet. It never throws on a failed repair -- it returns `applied: false` with
 * a `reason`, because "tried a rewrite and retention rejected it" is the single most useful thing to see
 * when a bullet will not hide.
 */
export async function ApplyRepair(backend: LLMBackend, repair: JudgeRepair, slots: JudgeLoopSlot[], slotIndex: number, options: ApplyRepairOptions): Promise<ApplyRepairResult> {
	const slot = slots[slotIndex];
	const isManual = options.source == "manual";
	// the text this attempt is starting FROM. For a manual retry that is the SANDBOX text (the played text with
	// earlier manual attempts replayed), so two retries in a row see the cumulative result rather than each
	// starting from the originally-played sentence.
	const textBefore = isManual ? ResolveSlotSandboxText(slot) : SlotDisplayText(slot);
	const createdAt = Date.now();

	const baseHistory: SlotRepairAttempt = {
		attemptIndex: options.attemptIndex,
		repair,
		source: options.source,
		textBefore,
		textAfter: null,
		applied: false,
		reason: "",
		newSourceId: null,
		outputTokens: 0,
		createdAt,
	};
	const finish = (applied: boolean, reason: string, updates: Partial<JudgeLoopSlot>, outputTokens: number, extra?: Partial<SlotRepairAttempt>): ApplyRepairResult=>{
		const history: SlotRepairAttempt = {
			...baseHistory,
			applied,
			reason,
			outputTokens,
			...(extra ?? {}),
		};
		const nextSlots = [...slots];
		nextSlots[slotIndex] = {
			...slot,
			// NON-DESTRUCTIVE for a manual retry (§16.0 and the "what the player read" rule): the debug view may
			// not rewrite the record of the round as it was played. `finalText`/`sourceId`/the counters describe
			// the ROUND, so a manual attempt accumulates in `repairHistory` only and the panel derives the
			// sandbox text from it (`ResolveSlotSandboxText`). Without this, pressing a retry button silently
			// changed the text the user had already read and scored.
			...(isManual ? {} : updates),
			repairHistory: [...(slot.repairHistory ?? []), history],
		};
		return {slots: nextSlots, applied, history, outputTokens};
	};

	if (repair == "none") {
		return finish(false, "no repair was requested", {}, 0);
	}

	if (repair == "rewriteReal") {
		const rewritten = await RepairRewrite(backend, {
			world: options.world,
			character: options.character,
			summary: options.summary,
			// the CURRENT text of every bullet, sandbox included, so a rewrite joins what the story now says
			bullets: slots.map(a=>({slotId: a.slotId, text: isManual ? ResolveSlotSandboxText(a) : SlotDisplayText(a)})),
			targetSlotId: slot.slotId,
			counter: options.counter ?? null,
			checkOptions: options.checkOptions,
		});
		if (rewritten.text == null) {
			// the rewrite produced nothing usable. Reported rather than swallowed: this is the case that
			// looks identical to "the repair was never attempted" from the round-level counts.
			return finish(false, `the rewrite produced nothing usable: ${rewritten.problems[0] ?? "no text"}`, {}, rewritten.outputTokens, {textAfter: null});
		}

		// retention gate (§9.3): a rewrite that replaced the event would ship a bullet labelled REAL
		// describing something the user never dreamt -- the §17 false-memory risk
		const sourceText = slot.sourceEventText ?? slot.reshapedText;
		const stillSameEvent = options.checkRetention == null
			? true
			: await options.checkRetention(sourceText, rewritten.text);
		if (!stillSameEvent) {
			// the REJECTED text is kept on the history entry on purpose: seeing what the model wrote and
			// why it was thrown away is the whole diagnostic value of this record
			return finish(false, "the rewrite changed the event, so it was rejected (retention)", {}, rewritten.outputTokens, {textAfter: rewritten.text});
		}

		return finish(true, "rewritten in place", {
			finalText: rewritten.text,
			rewriteCount: Math.max(0, Math.round(slot.rewriteCount ?? 0)) + 1,
		}, rewritten.outputTokens, {textAfter: rewritten.text});
	}

	if (repair == "resampleFake" || repair == "resampleReal") {
		const replacement = options.resampleSlot == null ? null : await options.resampleSlot(slot, repair);
		if (replacement == null) {
			return finish(false, repair == "resampleReal"
				? "no unused real bullet was available to resample in"
				: "the generator produced no usable replacement", {}, 0);
		}
		// a resample that hands back the SAME text replaced nothing, so recording it as applied would inflate
		// the round's counters and show an attempt whose `textAfter` equals its `textBefore` (which the
		// inspector then renders with no text at all). The generator redraws from a different corpus row, so a
		// repeat means the pool is exhausted rather than that the model echoed.
		if (replacement.text.trim() == textBefore.trim()) {
			return finish(false, "the replacement was the same text, so nothing changed", {}, replacement.outputTokens ?? 0, {textAfter: replacement.text});
		}
		return finish(true, repair == "resampleReal" ? "replaced with another real bullet" : "replaced with a freshly generated fake", {
			finalText: replacement.text,
			resampleCount: Math.max(0, Math.round(slot.resampleCount ?? 0)) + 1,
		}, replacement.outputTokens ?? 0, {textAfter: replacement.text, newSourceId: replacement.newSourceId ?? null});
	}

	return finish(false, `unknown repair "${repair}"`, {}, 0);
}

/** Re-runs the judge loop over a single slot's repaired story, for the debug view's manual retry.
 *
 * This is `RunJudgeLoop` with `maxAttempts: 1` and the repair FORCED (rather than chosen), which is what makes
 * it a meaningful experiment: "would a resample hide this one?" is answerable without the loop's own
 * heuristic choosing something else. `attemptIndexOffset` continues the history numbering so the user sees
 * their manual retry appended after the automatic attempts rather than replacing them.
 */
export async function RunSingleRepair(backend: LLMBackend, repair: JudgeRepair, slotId: string, options: JudgeLoopOptions): Promise<JudgeLoopResult> {
	const slotIndex = options.slots.findIndex(slot=>slot.slotId == slotId);
	if (slotIndex < 0) {
		throw new Error(`RunSingleRepair: slot "${slotId}" is not in this round.`);
	}

	const applied = await ApplyRepair(backend, repair, options.slots.map(slot=>({...slot})), slotIndex, {
		world: options.world,
		character: options.character,
		summary: options.summary,
		counter: options.counter ?? null,
		checkOptions: options.checkOptions,
		resampleSlot: options.resampleSlot ?? null,
		checkRetention: options.checkRetention ?? null,
		attemptIndex: Math.max(0, Math.round(options.attemptIndexOffset ?? 0)),
		source: "manual",
	});

// re-judge the repaired story ONCE, with the loop's own repair step DISABLED: the user asked "does THIS
		// repair help?", so the loop must not go on to choose a different repair of its own. Without
		// `allowRepairs: false` the single out-of-band attempt would trigger one, and the history would grow a
		// `loop` entry for a button the user never pressed.
		return RunJudgeLoop(backend, {
			...options,
			slots: applied.slots,
			settings: {...options.settings, maxAttempts: 1},
			attemptIndexOffset: Math.max(0, Math.round(options.attemptIndexOffset ?? 0)) + 1,
			allowRepairs: false,
		});
}

/** Where the NEXT repair-history entry should number from: one past the highest `attemptIndex` already on
 * record, whether that came from the automatic loop or from an earlier manual retry (§9.3).
 *
 * Derived from the HISTORY rather than from `judgeSummary.attempts` because the summary counts LOOP attempts
 * only -- using it would give every manual retry the same index (they all read "attempt 4"), making two
 * retries indistinguishable in the panel. */
export function NextRepairAttemptIndex(slots: RoundSlot[]|n): number {
	let max = -1;
	for (const slot of slots ?? []) {
		for (const entry of slot.repairHistory ?? []) {
			if (entry.attemptIndex > max) max = entry.attemptIndex;
		}
	}
	return max + 1;
}

/** The text a slot is CURRENTLY at in the debug sandbox: `finalText` (what the player actually read) with
 * every MANUAL history entry replayed on top of it (§9.3).
 *
 * This is the fix for "my retry changed what the player read": `finalText` is a historical record of the
 * round as it was played and must never be rewritten by a debugging action. A retry therefore accumulates in
 * the manual history only, and the panel shows this sandbox text separately from the played text.
 *
 * Loop entries are deliberately NOT replayed: they were applied before the round was shown, so `finalText`
 * already includes them. Replaying them would double-apply. */
export function ResolveSlotSandboxText(slot: RoundSlot): string {
	let text = SlotDisplayText(slot);
	for (const entry of slot.repairHistory ?? []) {
		if (entry.source != "manual") continue;
		if (!entry.applied) continue;
		if (entry.textAfter == null) continue;
		text = entry.textAfter;
	}
	return text;
}

/** The text a judge (or the reveal) should read for a slot: the repaired text if a rewrite landed, else
 * the woven text, else the reshaped one. Mirrors `Round.SlotText` deliberately -- this module must stay
 * importable without dragging in the view models, and the fallback chain is two lines. */
function SlotDisplayText(slot: RoundSlot): string {
	if (slot.finalText.length > 0) return slot.finalText;
	if (slot.wovenText.length > 0) return slot.wovenText;
	return slot.reshapedText;
}

/** A short human summary of a judge loop, for the reveal screen and the dev tool (§16.2 P10). */
export function DescribeJudgeLoopSummary(summary: JudgeLoopSummary|null|n): string {
	if (summary == null) return "not judged";
	const aucText = summary.auc == null ? "no usable score" : summary.auc.toFixed(2);
	const bandText = `${summary.bandMin.toFixed(2)}-${summary.bandMax.toFixed(2)}`;
	const head = summary.landedInBand
		? `stranger-blind (AUC ${aucText}, band ${bandText}) after ${summary.attempts} attempt(s)`
		: `could NOT be made stranger-blind (AUC ${aucText}, band ${bandText}) after ${summary.attempts} attempt(s)`;
	const repairParts = Object.entries(summary.repairs)
		.filter(([, count])=>(count ?? 0) > 0)
		.map(([kind, count])=>`${count}× ${DescribeRepair(kind as JudgeRepair)}`);
	const repairsText = repairParts.length == 0 ? "no repairs" : repairParts.join(", ");
	const heldOutText = summary.heldOutAuc == null ? "no held-out score" : `held-out AUC ${summary.heldOutAuc.toFixed(2)}`;
	return `${head} · ${repairsText} · ${heldOutText}`;
}

export function DescribeRepair(repair: JudgeRepair): string {
	switch (repair) {
		case "resampleFake": return "resampled a fake";
		case "resampleReal": return "resampled a real bullet";
		case "rewriteReal": return "rewrote a real bullet into the world";
		case "none": return "no repair";
	}
}

/** Validates a judge loop result against a JSON-shape sanity pass. Used by the dev tool, not by the loop
 * itself (the loop builds the shapes directly). Exported so a test can assert the shape without the DB. */
export function ValidateJudgeAttempt(attempt: JudgeAttempt): string[] {
	return ValidateJSON(attempt, {
		type: "object",
		required: ["judge", "scores"],
		properties: {
			judge: {type: "string"},
			scores: {type: "object"},
		},
	});
}

/** Truncated one-line preview of a judge's scores, for a log line. */
export function PreviewScores(scores: Record<string, number>|n): string {
	const entries = Object.entries(scores ?? {});
	if (entries.length == 0) return "(no scores)";
	return PreviewText(entries.map(([id, score])=>`${id}=${score.toFixed(2)}`).join(" "), 400);
}
